import BreadcrumbsHeaderHtml from "./BreadcrumbsHeaderHtml.js";
import IconWithDropdownMenuCmp from "../IconWithDropdownMenu/IconWithDropdownMenuCmp.js";
import {createOrgNameUrlTemplate, extractOrgNameFromUrl} from "../../js/util/UrlUtils.js";
import AccessLevel from "../../js/entities/AccessLevel.js";
import OrgSettingsPageViewMode from "../../pages/org-settings/OrgSettingsPageViewMode.js";

export default class BreadcrumbsHeaderCmp {
    /** @type {BreadcrumbsHeaderHtml} */
    #html = new BreadcrumbsHeaderHtml();
    /** @type {IconWithDropdownMenuCmp} */
    #orgMenu;
    /** @type {IconWithDropdownMenuCmp} */
    #injectionMenu;
    /** @type {IconWithDropdownMenuCmp} */
    #batchMenu;
    /** @type {IconWithDropdownMenuCmp} */
    #postMenu;
    #callbacks = {
        reportClick: () => {},
        methodInfoClick: () => {},
        onDeleteInjectionClick: () => {},
        onShareInjectionClick: () => {},
        onPropsInjectionClick: () => {},
        onExportInjectionClick: () => {},
        onShareBatchClick: ()=>{},
        onDeleteBatchClick: ()=>{},
        onSharePostClick: ()=>{},
        onDeletePostClick: ()=>{},
    }

    /** @param {HTMLElement} parent */
    init(parent){
        this.#html.init(parent);
    }

    /** @param {function} cb */
    onMethodInfoButtonClick (cb) {
        this.#callbacks.methodInfoClick = cb;
    }

    /** @param {function} cb */
    onReportButtonClick (cb) {
        this.#callbacks.reportClick = cb;
    }

    /** @param {function} cb */
    onDeleteInjectionClick (cb) {
        this.#callbacks.onDeleteInjectionClick = cb;
    }

    /** @param {function} cb */
    onShareInjectionClick (cb) {
        this.#callbacks.onShareInjectionClick = cb;
    }

    /** @param {function} cb */
    onPropsInjectionClick (cb) {
        this.#callbacks.onPropsInjectionClick = cb;
    }

    /** @param {function} cb */
    onExportInjectionClick (cb) {
        this.#callbacks.onExportInjectionClick = cb;
    }

    /** @param {function} cb */
    onShareBatchClick (cb) {
        this.#callbacks.onShareBatchClick = cb;
    }

    /** @param {function} cb */
    onDeleteBatchClick (cb) {
        this.#callbacks.onDeleteBatchClick = cb;
    }

    /** @param {function} cb */
    onSharePostClick (cb) {
        this.#callbacks.onSharePostClick = cb;
    }

    /** @param {function} cb */
    onDeletePostClick (cb) {
        this.#callbacks.onDeletePostClick = cb;
    }

    /**
     * @param {string} orgName
     * @param {Org?} org if this part of breadcrumbs is not last (for example in injection or batch page) we do not
     *                   create an org menu and do not send an org as a param
     */
    addOrgName(orgName, org){
        if (!orgName) return
        this.#html.addOrg(orgName);
        // If access level less than write we don't add org menu
        if (!org || org.userAccessLevel.lessThan(AccessLevel.WRITE)) return;
        // If it is personal org and billing is not accessible
        // When personal org will have more than 1 tab (not only billing), this condition should be removed
        if (!org || (org.isPersonalOrg() && !org.billingAccessible)) return;
        this.#orgMenu = new IconWithDropdownMenuCmp();
        this.#orgMenu.init(this.#html.getElementByDataId(BreadcrumbsHeaderCmp.MENU_ID.MENU_ORG), "arrow_drop_down");
        this.#orgMenu.show([
            {
                id: BreadcrumbsHeaderCmp.ACTION_ID.ORG_SETTINGS,
                name: 'Settings',
                iconName: "settings",
                disabled: false
            }
        ]);
        this.#orgMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.ORG_SETTINGS, ()=>{
            const org = createOrgNameUrlTemplate(extractOrgNameFromUrl());
            document.location.assign(`${org}/settings`)
        });
    }

    /** @param {InjectionJson} injection */
    addInjectionName(injection){
        if (!injection) return
        this.#injectionMenu = new IconWithDropdownMenuCmp();
        if (injection.getBatchMeta())
            this.addBatchName(injection.getBatchMeta())
        this.#html.addInjectionName(injection.getDisplayName());
        this.#injectionMenu.init(this.#html.getElementByDataId(BreadcrumbsHeaderCmp.MENU_ID.MENU_INJECTION), "arrow_drop_down");
        this.#injectionMenu.show([
            {
                id: BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_INFO,
                name: 'Injection info',
                iconName: "info",
            },{
                id: BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_REPORT,
                name: 'Report',
                iconName: "picture_as_pdf",
            },{
                id: BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_SHARE,
                name: 'Share',
                hidden: injection.userAccessLevel.lessThan(AccessLevel.ADMIN) || !extractOrgNameFromUrl(),
                iconName: "share",
            },{
                id: BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_PROPS,
                name: 'Properties',
                iconName: "list_alt",
            },{
                id: BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_EXPORT,
                name: 'Export',
                iconName: "download",
            }
            ,{
                id: BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_DELETE,
                name: 'Delete',
                hidden: injection.userAccessLevel.lessThan(AccessLevel.ADMIN),
                iconName: "delete",
            }
        ]);
        this.#injectionMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_INFO, this.#callbacks.methodInfoClick.bind(this));
        this.#injectionMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_REPORT, this.#callbacks.reportClick.bind(this));
        this.#injectionMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_DELETE, this.#callbacks.onDeleteInjectionClick.bind(this, injection));
        this.#injectionMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_SHARE, this.#callbacks.onShareInjectionClick.bind(this, injection));
        this.#injectionMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_PROPS, this.#callbacks.onPropsInjectionClick.bind(this, injection));
        this.#injectionMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.INJECTION_EXPORT, this.#callbacks.onExportInjectionClick.bind(this, injection));
    }

    /** @param {BatchMeta} batchMeta */
    addBatchName(batchMeta){
        if (!batchMeta) return
        if (extractOrgNameFromUrl())
            this.#html.addBatchList(extractOrgNameFromUrl());
        this.#html.addBatchName(batchMeta.name, batchMeta.id);
        if (!this.#injectionMenu && batchMeta.isWritable()) {
            this.#batchMenu = new IconWithDropdownMenuCmp();
            this.#batchMenu.init(this.#html.getElementByDataId(BreadcrumbsHeaderCmp.MENU_ID.MENU_BATCH), "arrow_drop_down");
            this.#batchMenu.show([
                {
                    id: BreadcrumbsHeaderCmp.ACTION_ID.BATCH_EDIT,
                    name: 'Edit injection list',
                    iconName: "format_list_bulleted",
                    disabled: false
                },{
                    id: BreadcrumbsHeaderCmp.ACTION_ID.BATCH_SHARE,
                    name: 'Share',
                    iconName: "share",
                    hidden: batchMeta.userAccessLevel.lessThan(AccessLevel.ADMIN) || !extractOrgNameFromUrl()
                },{
                    id: BreadcrumbsHeaderCmp.ACTION_ID.BATCH_DELETE,
                    name: 'Delete',
                    iconName: "delete",
                    hidden: batchMeta.userAccessLevel.lessThan(AccessLevel.ADMIN)
                }
            ]);
            this.#batchMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.BATCH_EDIT, this.#openReassignBatchPage.bind(this, batchMeta))
            this.#batchMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.BATCH_SHARE, this.#callbacks.onShareBatchClick.bind(this, batchMeta))
            this.#batchMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.BATCH_DELETE, this.#callbacks.onDeleteBatchClick.bind(this, batchMeta.id))
        }
    }

    /** @param {{name: string, breadcrumbsName: string}} screenMode */
    addSettingsScreenName(screenMode){
        this.#html.addSettingsName();
        this.#html.addSettingsScreenName(screenMode);
    }

    /** @param {string} name */
    addTeamName(name){
        this.addSettingsScreenName(OrgSettingsPageViewMode.NAMES.TEAMS_LIST)
        this.#html.addTeamName(name);
    }

    /** @param {Post} post */
    addPostTitle(post){
        if (extractOrgNameFromUrl())
            this.#html.addPostList(extractOrgNameFromUrl());
        if (!post)
            return
        this.#html.addPostTitle(post);
        if (post.userAccessLevel.meetsOrExceeds(AccessLevel.ADMIN)){
            this.#postMenu = new IconWithDropdownMenuCmp();
            this.#postMenu.init(this.#html.getElementByDataId(BreadcrumbsHeaderCmp.MENU_ID.MENU_POST), "arrow_drop_down");
            this.#postMenu.show([
                {
                    id: BreadcrumbsHeaderCmp.ACTION_ID.POST_SHARE,
                    name: 'Share',
                    iconName: "share",
                    hidden: post.userAccessLevel.lessThan(AccessLevel.ADMIN)
                },{
                    id: BreadcrumbsHeaderCmp.ACTION_ID.POST_DELETE,
                    name: 'Delete',
                    iconName: "delete",
                    hidden: post.userAccessLevel.lessThan(AccessLevel.ADMIN)
                }
            ]);
            this.#postMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.POST_SHARE, this.#callbacks.onSharePostClick.bind(this))
            this.#postMenu.onItemClick(BreadcrumbsHeaderCmp.ACTION_ID.POST_DELETE, this.#callbacks.onDeletePostClick.bind(this))
        }

    }

    /** @param {string?} text */
    addEditItem(text){
        this.#html.addEditItem(text)
    }

    /** @param {string} hash */
    addHashToBatchUrl(hash){
        this.#html.addHashToBatchUrl(hash)
    }

    /** @param {BatchMeta} batchMeta */
    #openReassignBatchPage(batchMeta){
        const org = createOrgNameUrlTemplate(extractOrgNameFromUrl());
        document.location.assign(`${org}/batch/${batchMeta.id}/edit`)
    }

    static ACTION_ID = Object.freeze({
        "INJECTION_DELETE": "injection-delete",
        "INJECTION_INFO": "injection-info",
        "INJECTION_REPORT": "injection-report",
        "INJECTION_EXPORT": "injection-export",
        "INJECTION_SHARE": "injection-share",
        "INJECTION_PROPS": "injection-props",
        "ORG_SETTINGS": "org-settings",
        "BATCH_EDIT": "batch-edit",
        "BATCH_SHARE": "batch-share",
        "BATCH_DELETE": "batch-delete",
        "POST_SHARE": "post-share",
        "POST_DELETE": "post-delete",
    });

    static BREADCRUMBS_ITEM_ID = Object.freeze({
        "SETTINGS": "breadcrumbs-settings",
        "SETTINGS_SCREEN": "breadcrumbs-settings-screen",
        "TEAM": "breadcrumbs-team",
        "INJECTION": "breadcrumbs-injection",
        "INJECTION_LIST": "breadcrumbs-injection-list",
        "BATCH": "breadcrumbs-batch",
        "BATCH_LIST": "breadcrumbs-batch-list",
        "BATCH_LINK": "breadcrumbs-batch-link",
        "ITEM_EDIT": "breadcrumbs-batch-edit",
        "ORG": "breadcrumbs-org",
        "POST": "breadcrumbs-post",
        "POST_LIST": "breadcrumbs-post-list",
    });

    static MENU_ID = Object.freeze({
        "MENU_ORG": "org-menu",
        "MENU_INJECTION": "injection-menu",
        "MENU_BATCH": "batch-menu",
        "MENU_POST": "post-menu",
    });
}